/**
 * SchemaPruner — Caps JSON-LD on the page after SPA navigations.
 *
 * Problem in prod (audit 2026-06-12):
 *   Many ad-hoc JSON-LD injectors do `document.createElement('script')`
 *   inside a `useEffect` but never return a cleanup function. When the
 *   component unmounts on SPA navigation, its `<script>` stays in the
 *   <head> as an orphan. After 3–4 navigations, blog pages were measuring
 *   ~16 JSON-LD blocks and city pages ~10, while templates only declared
 *   4–6.
 *
 * Strategy (works without modifying every injector):
 *   1. useLayoutEffect (parent, runs AFTER child layoutEffects but BEFORE
 *      child useEffects) — stamp every existing JSON-LD <script> with
 *      `data-stale-gen` keyed to the current path. These scripts belong
 *      to the previous page.
 *   2. Children's useEffects then run. Re-mounted injectors first remove
 *      their own scripts (their existing cleanup pattern) then create
 *      *fresh* scripts which carry NO `data-stale-gen` attribute.
 *   3. parent useEffect schedules a sweep ~800ms later: any script that
 *      still bears `data-stale-gen` for this generation is orphaned
 *      (its owning component is no longer mounted) → remove it.
 *   4. Final hard cap: at most 6 JSON-LD blocks per page. If more
 *      remain, dedupe by `@id` / `@type+name`, then keep the most
 *      recent (current page's) and drop the rest.
 *
 * Mount once in MainLayout, after EnterpriseSchemas.
 */
import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_JSONLD = 6;
const SWEEP_DELAYS_MS = [400, 1200, 2500];

function getSchemaKey(script: Element): string {
  try {
    const parsed = JSON.parse(script.textContent || '');
    const items = Array.isArray(parsed)
      ? parsed
      : parsed['@graph']
        ? parsed['@graph']
        : [parsed];
    const first = items[0] || {};
    return first['@id'] || `${first['@type'] || 'unknown'}::${first.name || ''}`;
  } catch {
    return 'invalid';
  }
}

function capJsonLd() {
  const scripts = Array.from(
    document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]:not([data-schema-pin])'
    )
  );
  // Dedupe by schema key — keep the LAST occurrence (most recently injected = current page)
  const lastByKey = new Map<string, HTMLScriptElement>();
  for (const s of scripts) {
    lastByKey.set(getSchemaKey(s), s);
  }
  // Remove duplicates
  for (const s of scripts) {
    const key = getSchemaKey(s);
    if (lastByKey.get(key) !== s) s.remove();
  }
  // Hard cap: keep the LAST MAX_JSONLD (most recently injected)
  const remaining = Array.from(
    document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]:not([data-schema-pin])'
    )
  );
  if (remaining.length > MAX_JSONLD) {
    const excess = remaining.slice(0, remaining.length - MAX_JSONLD);
    excess.forEach(s => s.remove());
  }
}

export function SchemaPruner() {
  const location = useLocation();

  // Phase 1 — stamp pre-existing scripts BEFORE child useEffects re-fire.
  useLayoutEffect(() => {
    const generation = `gen-${location.pathname}-${Date.now()}`;
    const existing = document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]:not([data-schema-pin])'
    );
    existing.forEach(s => {
      // Only stamp scripts that don't yet have a generation marker.
      // Stamped = "belongs to the previous page".
      if (!s.hasAttribute('data-stale-gen')) {
        s.setAttribute('data-stale-gen', generation);
      }
    });

    return () => {
      // No-op; the cleanup happens in the next useEffect via timers.
    };
  }, [location.pathname]);

  // Phase 2 — after children re-injected (or didn't), sweep stamped orphans + cap.
  useEffect(() => {
    const timers = SWEEP_DELAYS_MS.map(delay =>
      window.setTimeout(() => {
        // Remove any script that still bears a data-stale-gen marker:
        // freshly created scripts from current-page components have no marker.
        document.head
          .querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"][data-stale-gen]:not([data-schema-pin])')
          .forEach(s => s.remove());
        // Hard cap & dedupe
        capJsonLd();
      }, delay)
    );

    return () => {
      timers.forEach(t => window.clearTimeout(t));
    };
  }, [location.pathname]);

  return null;
}
